/**
 * Financials page — hosts the deterministic financial modules for a selected deal:
 *   M1 Classify Financials · M2 Quality of Earnings · M3 LBO · M4/5 DCF + Monte Carlo.
 *
 * These were moved off the Due Diligence (/ai-findings) page into their own tab. Each panel
 * is a standalone component keyed on dealId; this page only owns deal selection + accordions.
 */

import React, { useState, useEffect } from 'react';
import { Calculator, ChevronDown, ChevronUp } from 'lucide-react';
import ClassificationReview from './ClassificationReview';
import QofEPanel from './QofEPanel';
import LBOPanel from './LBOPanel';
import DCFPanel from './DCFPanel';
import { dealsApi } from '../services/api';
import './AIFindings.css';

export default function FinancialsPage({ selectedDeal }) {
  const [deals, setDeals] = useState([]);
  const [dealId, setDealId] = useState(selectedDeal?.id || null);
  const [showClassify, setShowClassify] = useState(false);
  const [showQofE, setShowQofE] = useState(false);
  const [showLBO, setShowLBO] = useState(false);
  const [showDCF, setShowDCF] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await dealsApi.list();
        const data = res.data || [];
        if (!alive) return;
        setDeals(data);
        setDealId(prev => prev || selectedDeal?.id || data[0]?.id || null);
      } catch (e) {
        console.error('Error fetching deals:', e);
      }
    })();
    return () => { alive = false; };
  }, [selectedDeal]);

  // Preselect when navigated from a specific deal context.
  useEffect(() => {
    if (selectedDeal?.id) setDealId(selectedDeal.id);
  }, [selectedDeal]);

  const panel = { border: '1px solid #e2e8f0', borderRadius: 12, marginTop: 12 };

  return (
    <div className="aifindings-container">
      <div className="aifindings-header">
        <div className="page-title">
          <div className="page-title-icon">
            <Calculator size={20} />
          </div>
          <div className="page-title-text">
            <h1>Financials</h1>
            <span className="page-subtitle">Classification, Quality of Earnings, LBO and DCF / Monte Carlo</span>
          </div>
        </div>
      </div>

      <div className="analysis-controls">
        <div className="control-group">
          <label>Select Deal:</label>
          <select
            className="target-select"
            value={dealId || ''}
            onChange={(e) => setDealId(e.target.value)}
          >
            {deals.map(deal => (
              <option key={deal.id} value={deal.id}>{deal.name}</option>
            ))}
          </select>
        </div>

        {dealId && (
          <>
            <button className="btn-history" onClick={() => setShowClassify(s => !s)}>
              Classify Financials {showClassify ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
            </button>
            <button className="btn-history" onClick={() => setShowQofE(s => !s)}>
              Quality of Earnings {showQofE ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
            </button>
            <button className="btn-history" onClick={() => setShowLBO(s => !s)}>
              LBO Model {showLBO ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
            </button>
            <button className="btn-history" onClick={() => setShowDCF(s => !s)}>
              DCF / Monte Carlo {showDCF ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
            </button>
          </>
        )}
      </div>

      {!dealId && <div style={{ padding: 20, color: '#64748b' }}>Select a deal to view its financial models.</div>}

      {dealId && showClassify && (
        <div className="classification-review-panel" style={panel}><ClassificationReview dealId={dealId} /></div>
      )}
      {dealId && showQofE && (
        <div className="qofe-panel" style={panel}><QofEPanel dealId={dealId} /></div>
      )}
      {dealId && showLBO && (
        <div className="lbo-panel" style={panel}><LBOPanel dealId={dealId} /></div>
      )}
      {dealId && showDCF && (
        <div className="dcf-panel" style={panel}><DCFPanel dealId={dealId} /></div>
      )}
    </div>
  );
}
